import * as z from 'zod';

import { contactQueryLinkerSchema } from '@/_pages/contacts/shared/schemas';
import { domainQueryLinkerSchema } from '@/_pages/domains/shared/schemas';
import { agreementStatusFormSchema } from '@/_schemas/agreements/agreement-status-schema';
import { pipelineFormSchema } from '@/_schemas/agreements/agreements-pipeline-schemas';
import { booleanFormSchema, numericStringFormSchema } from '@/_schemas/generic';
import { teamMemberQueryLinkerWithContactInfoSchema } from '@/_schemas/team-members/team-members-query-linker-with-contact-info-schema';

import { agreementQuerySchema } from './agreements-query-schema';
import { commissionsFormSchema } from './commissions-schemas';
import { clientQuerySchemaAgreementsLinker } from './linked-query-schemas';
import { subsidyFormSchema } from './subsidy-schemas';

import { contractingPartyFormSchema } from '../components/contracting-party/schema';
import { deliverablesFormSchema } from '../deliverables';
import { agreementInstallmentFormSchema } from '../installments/schemas/agreement-installments-form-schema';

export const agreementFormSchemaRaw = agreementQuerySchema
  .omit({
    status: true,
    pipeline: true,
    client_id: true,
    primary_domains: true,
    account_manager: true,
    agreement_deliverables: true,
    opt_in: true,
    one_time_total: true,
    opt_in_total: true,
    monthly_recurring_total: true,
    annual_recurring_total: true,
    billing_term_id: true,
    terms_condition_template_id: true,
    agreement_signer_id: true,
    brand_id: true,
    partner_id: true,
    agreement_installments: true,
    exclude_from_averages: true,
  })
  .extend({
    status: agreementStatusFormSchema.nullable(),
    agreement_date: z.string().refine((val) => !!val, {
      error: 'Agreement date is required',
    }),
    expiry_date: z.string().refine((val) => !!val, {
      error: 'Expiry date is required',
    }),
    client: clientQuerySchemaAgreementsLinker,
    account_manager: z.array(teamMemberQueryLinkerWithContactInfoSchema).refine((val) => val?.length > 0, {
      error: 'Account manager is required',
    }),
    primary_domains: domainQueryLinkerSchema.nullable(),

    wholesale_price_enabled: booleanFormSchema(),
    agreement_deliverables: z.array(deliverablesFormSchema),
    tempAgreement_deliverables: z.array(deliverablesFormSchema),

    emergency_contact: z.array(contactQueryLinkerSchema).nullable(),
    billing_email_contacts: z.array(contactQueryLinkerSchema).nullable(),
    agreement_contact: contactQueryLinkerSchema.nullable(),
    project_contact: contactQueryLinkerSchema.nullable(),
    billing_contact: contactQueryLinkerSchema.nullable(),
    harvest: numericStringFormSchema(),

    execution_date: z.string().nullish(),
    implementation_start_date: z.string().nullish(),
    go_live_date: z.string().nullish(),
    monthly_recurring_start_date: z.string().nullish(),
    monthly_recurring_stop_date: z.string().nullish(),
    annual_recurring_start_date: z.string().nullish(),
    annual_recurring_stop_date: z.string().nullish(),

    agreement_installments: z.array(agreementInstallmentFormSchema),

    pipeline: pipelineFormSchema.nullable(),

    exclude_from_averages: booleanFormSchema(),

    subsidies: z.array(subsidyFormSchema).nullish(),
    enable_commission: booleanFormSchema(),
    agreement_commissions: z.array(commissionsFormSchema).nullable(),
    contracting_party: contractingPartyFormSchema.nullable(),

    orphan_invoicing: booleanFormSchema(),
  });

export const agreementFormSchema = agreementFormSchemaRaw.refine(
  ({ parent_id, pipeline }) => {
    if (parent_id && !pipeline) {
      return true;
    }

    return !!pipeline;
  },
  {
    message: 'Pipeline is required.',
    path: ['pipeline'],
  }
);

export type AgreementFormSchema = z.infer<typeof agreementFormSchemaRaw>;

export const agreementNewFormSchema = agreementFormSchemaRaw
  .pick({
    client: true,
    agreement_date: true,
    expiry_date: true,
    primary_domains: true,
    pipeline: true,
    account_manager: true,
    agreement_id: true,
    status: true,
    billing_term: true,
    addresses: true,
    parent_id: true,
    brand: true,
    partner: true,
  })
  .extend({
    client: clientQuerySchemaAgreementsLinker
      .pick({
        id: true,
        domains: true,
        name: true,
        logo: true,
        status: true,
        account_number: true,
        brand: true,
        partner: true,
      })
      .nullable()
      .refine(
        (val) => {
          if (val) {
            return val.id !== -1;
          }

          return !!val;
        },
        { message: 'Company is required' }
      ),
  })
  .refine(
    ({ parent_id, pipeline }) => {
      if (parent_id && !pipeline) {
        return true;
      }

      return !!pipeline;
    },
    {
      message: 'Pipeline is required.',
      path: ['pipeline'],
    }
  );

export type AgreementNewFormSchema = z.infer<typeof agreementNewFormSchema>;

export const agreementLockSnapshotSchema = agreementFormSchemaRaw.pick({
  execution_date: true,
  go_live_date: true,
  // agreement_date: true,
  implementation_start_date: true,
  monthly_recurring_start_date: true,
  monthly_recurring_stop_date: true,
  annual_recurring_start_date: true,
  annual_recurring_stop_date: true,
});

export type AgreementLockSnapshot = z.infer<typeof agreementLockSnapshotSchema>;
